import { logger, generateRequestId } from '../utils/logger';

/**
 * Currency conversion service with automatic geolocation detection
 * Provides real-time currency conversion for global payment support
 */

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: 'CA$',
  AUD: 'A$',
  JPY: '¥',
  INR: '₹',
  CNY: '¥',
  BRL: 'R$',
  ZAR: 'R'
};

const SUPPORTED_CURRENCIES = [
  { code: 'GBP', name: 'British Pound', countries: ['GB'] },
  { code: 'USD', name: 'US Dollar', countries: ['US'] },
  { code: 'EUR', name: 'Euro', countries: ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'IE', 'PT', 'FI', 'GR'] },
  { code: 'CAD', name: 'Canadian Dollar', countries: ['CA'] },
  { code: 'AUD', name: 'Australian Dollar', countries: ['AU'] },
  { code: 'JPY', name: 'Japanese Yen', countries: ['JP'] },
  { code: 'INR', name: 'Indian Rupee', countries: ['IN'] },
  { code: 'CNY', name: 'Chinese Yuan', countries: ['CN'] },
  { code: 'BRL', name: 'Brazilian Real', countries: ['BR'] },
  { code: 'ZAR', name: 'South African Rand', countries: ['ZA'] }
];

export const currencyService = {
  /**
   * Detect user's currency based on geolocation
   * @returns {Promise<{currency: string, country: string}>}
   */
  async detectUserCurrency() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      // Try to get user location from IP geolocation API
      const response = await fetch('https://ipapi.co/json/');
      const data = await response?.json();
      
      const countryCode = data?.country_code;
      
      // Find currency for country
      const currencyInfo = SUPPORTED_CURRENCIES?.find(c => 
        c?.countries?.includes(countryCode)
      );

      const detectedCurrency = currencyInfo?.code || 'GBP'; // Default to GBP
      
      logger?.debug('Currency detected', { requestId, currency: detectedCurrency, country: countryCode });
      
      // Store in localStorage for future use
      localStorage?.setItem('user_currency', detectedCurrency);
      localStorage?.setItem('user_country', countryCode);
      
      return { currency: detectedCurrency, country: countryCode };
    } catch (error) {
      logger?.error('Currency detection failed', error, { requestId });
      // Fallback to stored or default currency
      const storedCurrency = localStorage?.getItem('user_currency') || 'GBP';
      return { currency: storedCurrency, country: 'GB' };
    }
  },

  /**
   * Get stored user currency or detect if not available
   * @returns {Promise<string>}
   */
  async getUserCurrency() {
    const stored = localStorage?.getItem('user_currency');
    if (stored) {
      return stored;
    }
    
    const { currency } = await this.detectUserCurrency();
    return currency;
  },

  /**
   * Get exchange rates from base currency (GBP)
   * @returns {Promise<Object>}
   */
  async getExchangeRates() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      // Check cache first (valid for 1 hour)
      const cached = localStorage?.getItem('exchange_rates');
      const cacheTime = localStorage?.getItem('exchange_rates_time');
      
      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < 3600000) { // 1 hour
          return JSON.parse(cached);
        }
      }

      // Fetch fresh rates from API
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/GBP');
      const data = await response?.json();
      
      const rates = data?.rates || {};
      
      // Cache the rates
      localStorage?.setItem('exchange_rates', JSON.stringify(rates));
      localStorage?.setItem('exchange_rates_time', Date.now()?.toString());
      
      logger?.debug('Exchange rates fetched', { requestId, ratesCount: Object.keys(rates)?.length });
      
      return rates;
    } catch (error) {
      logger?.error('Failed to fetch exchange rates', error, { requestId });
      // Return fallback rates
      return {
        GBP: 1,
        USD: 1.27,
        EUR: 1.17,
        CAD: 1.71,
        AUD: 1.91,
        JPY: 188.5,
        INR: 106.8,
        CNY: 9.15,
        BRL: 7.32,
        ZAR: 23.1
      };
    }
  },

  /**
   * Convert amount from GBP to target currency
   * @param {number} amountGBP - Amount in GBP
   * @param {string} targetCurrency - Target currency code
   * @returns {Promise<number>}
   */
  async convertFromGBP(amountGBP, targetCurrency) {
    if (targetCurrency === 'GBP') {
      return amountGBP;
    }

    const rates = await this.getExchangeRates();
    const rate = rates?.[targetCurrency] || 1;
    
    return parseFloat((amountGBP * rate)?.toFixed(2));
  },

  /**
   * Format price with currency symbol
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code
   * @returns {string}
   */
  formatPrice(amount, currency) {
    const symbol = CURRENCY_SYMBOLS?.[currency] || currency;
    return `${symbol}${amount?.toFixed(2)}`;
  },

  /**
   * Get all supported currencies
   * @returns {Array}
   */
  getSupportedCurrencies() {
    return SUPPORTED_CURRENCIES;
  },

  /**
   * Manually set user currency
   * @param {string} currency - Currency code
   */
  setUserCurrency(currency) {
    localStorage?.setItem('user_currency', currency);
    logger?.debug('User currency manually set', { currency });
  }
};

export default currencyService;